<script lang="ts">
  import { createEventDispatcher } from 'svelte';
  import Icon from '@iconify/svelte';
  import { Button } from 'flowbite-svelte';
  import MediaManager from './MediaManager.svelte';

  export let imagePath: string = '';
  export let imageUrl: string = '';
  export let targetField: string | null = null;
  export let bucketName: string = 'content';
  export let folderPath: string = '';
  export let title: string = 'Select Image';
  export let acceptedFileTypes: string = 'image/*';
  export let height: string = '200px';
  export let width: string = '100%';
  export let placeholderText: string = 'No image selected';
  export let buttonText: string = 'Select Image';
  export let rounded: boolean = true;
  export let border: boolean = true;
  export let showFilename: boolean = true;
  export let extractFilename: (path: string) => string = (path) => path.split('/').pop() || '';

  const dispatch = createEventDispatcher<{
    select: { filePath: string, publicUrl: string, filename: string };
    delete: { filePath: string };
    change: { field: string, value: string };
  }>();

  let showMediaManager = false;

  $: filename = imagePath ? imagePath.split('/').pop() : '';

  function handleFileSelect(event: CustomEvent) {
    imagePath = event.detail.filePath;
    imageUrl = event.detail.publicUrl;

    const extractedFilename = extractFilename(event.detail.filePath);

    dispatch('select', {
      filePath: event.detail.filePath,
      publicUrl: event.detail.publicUrl,
      filename: extractedFilename
    });

    if (targetField !== null) {
      dispatch('change', {
        field: targetField,
        value: extractedFilename
      });
    }
  }

  function handleFileDelete(event: CustomEvent) {
    if (imagePath === event.detail.filePath) {
      const oldPath = imagePath;
      imagePath = '';
      imageUrl = '';

      dispatch('delete', { filePath: oldPath });

      if (targetField !== null) {
        dispatch('change', {
          field: targetField,
          value: ''
        });
      }
    }
  }

  function removeImage() {
    if (imagePath) {
      const oldPath = imagePath;
      imagePath = '';
      imageUrl = '';

      dispatch('delete', { filePath: oldPath });

      if (targetField !== null) {
        dispatch('change', {
          field: targetField,
          value: ''
        });
      }
    }
  }
</script>

<div class="flex flex-col">
  <div
  class="relative bg-white transparent-effect {border ? 'border border-gray-200' : ''} {rounded ? 'rounded-lg' : ''} overflow-hidden"
  style="height: {height}; width: {width};"
  >
    {#if imageUrl}
      <div class="relative h-full w-full group">
        <img
          src={imageUrl}
          alt="Selected asset"
          class="h-full w-full object-contain"
        />
        <div class="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-all flex items-center justify-center opacity-0 group-hover:opacity-100">
          <div class="flex gap-2">
            <Button
              size="sm"
              color="blue"
              on:click={() => showMediaManager = true}
            >
              <Icon icon="heroicons:pencil" class="mr-1" />
              Change
            </Button>
            <Button
              size="sm"
              color="red"
              on:click={removeImage}
            >
              <Icon icon="heroicons:trash" class="mr-1" />
              Remove
            </Button>
          </div>
        </div>
      </div>
    {:else}
      <div class="h-full w-full flex flex-col items-center justify-center bg-gray-50 p-4">
        <Icon icon="heroicons:photo" class="text-4xl text-gray-400 mb-2" />
        <p class="text-gray-500 text-sm text-center mb-4">{placeholderText}</p>
        <Button
          size="sm"
          color="blue"
          on:click={() => showMediaManager = true}
        >
          <Icon icon="heroicons:plus" class="mr-1" />
          {buttonText}
        </Button>
      </div>
    {/if}
  </div>

  {#if showFilename && filename}
    <div class="mt-2 text-center">
      <p class="text-xs text-gray-500 truncate max-w-full" title={filename}>{filename}</p>
    </div>
  {/if}
</div>

<MediaManager
  bind:open={showMediaManager}
  bucketName={bucketName}
  folderPath={folderPath}
  title={title}
  selectedFilePath={imagePath}
  acceptedFileTypes={acceptedFileTypes}
  on:select={handleFileSelect}
  on:delete={handleFileDelete}
/>

<style>
  .transparent-effect{
    opacity: 0.8;
    background-image:  repeating-linear-gradient(45deg, #1d1d1d 25%, transparent 25%, transparent 75%, #1d1d1d 75%, #1d1d1d), repeating-linear-gradient(45deg, #1d1d1d 25%, #383838 25%, #383838 75%, #1d1d1d 75%, #1d1d1d);
    background-position: 0 0, 10px 10px;
    background-size: 20px 20px;
  }


</style>
